import React from 'react';
import 'leaflet/dist/leaflet.css';
import type { RouteRecord } from 'vite-react-ssg';
import AppLayout from './components/AppLayout';
import { useAuthModal } from './contexts/AuthModalContext';
import Index from './pages/Index';
import About from './pages/About';
import Contact from './pages/Contact';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import MilkyWayCalendar from './pages/MilkyWayCalendar';
import Features from './pages/Features';
import Dashboard from './pages/Dashboard';
import Admin from './pages/Admin';
import Register from './pages/Register';
import Onboarding from './pages/Onboarding';
import Success from './pages/Success';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Security from './pages/Security';
import SharedCalendar from './pages/SharedCalendar';
import SharedTrip from './pages/SharedTrip';
import Resources from './pages/Resources';
import MilkyWayCalendarAnnual from './pages/MilkyWayCalendarAnnual';
import { SUPPORTED_YEARS } from './data/annual-calendar/types';
import { Navigate, useParams } from 'react-router-dom';
import { currentCalendarYear } from './lib/calendarYear';
import { SEOHead } from './components/SEOHead';
import { SHARED_PAGE_SHELL_SLUG } from './lib/sharedPage';

function CurrentCalendarRedirect() {
  // Live requests 307 to the current year in middleware.ts. The prerender
  // still has to be a real document: this route used to emit an empty shell.
  return (
    <>
      <SEOHead
        title="Milky Way Calendar | Milky Way Planner"
        description="The Milky Way calendar opens on the current year: Saturday-night visibility for dark-sky locations, scored on galactic core position and moon phase."
        canonicalUrl="/milky-way-calendar"
        noindex
      />
      <h1 className="text-3xl font-bold text-foreground">Milky Way Calendar</h1>
      <Navigate to={`/milky-way-calendar/${currentCalendarYear()}`} replace />
    </>
  );
}

// Redirect /learn/:slug → /intro-to-astrophotography/:slug
const LearnLessonRedirect: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/intro-to-astrophotography/${slug}`} replace />;
};
import DonationThanks from './pages/DonationThanks';
import Donate from './pages/Donate';
import NotFound from './pages/NotFound';
import Workshops from './pages/Workshops';
import WorkshopDetail from './pages/WorkshopDetail';
import WorkshopManage from './pages/WorkshopManage';
import WorkshopSignup from './pages/WorkshopSignup';
import Instructors from './pages/Instructors';
import InstructorProfile from './pages/InstructorProfile';
import Locations from './pages/Locations';
import LocationDetail from './pages/LocationDetail';
import LocationHub from './pages/LocationHub';
import LightPollution from './pages/LightPollution';
import Learn from './pages/Learn';
import LearnLesson from './pages/LearnLesson';
import Changelog from './pages/Changelog';
import LocationGuidePage from './pages/LocationGuidePage';

// Wrapper component for pages that accept onShowAuthModal prop
function AuthPage({
  Component,
}: {
  Component: React.ComponentType<{ onShowAuthModal: () => void }>;
}) {
  const { openAuthModal } = useAuthModal();
  return <Component onShowAuthModal={openAuthModal} />;
}


export const routes: RouteRecord[] = [
  {
    path: '/',
    element: <AppLayout />,
    children: [
      { index: true, Component: Index },
      { path: 'about', Component: About },
      {
        path: 'features',
        element: <AuthPage Component={Features} />,
      },
      { path: 'contact', Component: Contact },
      { path: 'planner', Component: MilkyWayCalendar },
      {
        path: 'milky-way-calendar',
        element: <CurrentCalendarRedirect />,
      },
      {
        path: 'milky-way-calendar/:year',
        Component: MilkyWayCalendarAnnual,
        getStaticPaths: async () =>
          SUPPORTED_YEARS.map((y) => `milky-way-calendar/${y}`),
      },
      { path: 'blog', Component: Blog },
      {
        path: 'blog/:slug',
        Component: BlogPost,
        getStaticPaths: async () => {
          const { getAllSlugs } = await import('./lib/blog');
          return getAllSlugs().map((slug) => `blog/${slug}`);
        },
      },
      { path: 'intro-to-astrophotography', Component: Learn },
      { path: 'learn', element: <Navigate to="/intro-to-astrophotography" replace /> },
      { path: 'learn/:slug', Component: LearnLessonRedirect },
      {
        path: 'intro-to-astrophotography/:slug',
        Component: LearnLesson,
        getStaticPaths: async () => {
          const { getAllSlugs } = await import('./lib/learn');
          return getAllSlugs().map((slug) => `intro-to-astrophotography/${slug}`);
        },
      },
      {
        path: 'moon-planner',
        element: <Navigate to="/features" replace />,
      },
      { path: 'success', Component: Success },
      { path: 'onboarding', Component: Onboarding },
      { path: 'register', Component: Register },
      { path: 'admin', Component: Admin },
      { path: 'dashboard', Component: Dashboard },
      // Shared calendars and trips are made at runtime. The build prerenders
      // one shell for each, the page in its loading state, which middleware.ts
      // serves for every slug so React hydrates matching markup (ERI-2783).
      {
        path: 'calendar/:slug',
        Component: SharedCalendar,
        getStaticPaths: async () => [`calendar/${SHARED_PAGE_SHELL_SLUG}`],
      },
      {
        path: 'trip/:slug',
        Component: SharedTrip,
        getStaticPaths: async () => [`trip/${SHARED_PAGE_SHELL_SLUG}`],
      },
      { path: 'donate', Component: Donate },
      { path: 'donate/thanks', Component: DonationThanks },
      { path: 'workshops', Component: Workshops },
      { path: 'workshops/manage', Component: WorkshopManage },
      { path: 'workshops/list-your-workshop', Component: WorkshopSignup },
      { path: 'workshops/instructors', Component: Instructors },
      { path: 'workshops/instructors/:slug', Component: InstructorProfile },
      { path: 'workshops/:slug', Component: WorkshopDetail },
      { path: 'light-pollution', Component: LightPollution },
      // Locations come from the committed snapshot (src/data/locations.json). The
      // loaders run at build time, so each page's HTML carries its content; the
      // browser later reads the build's per-page data files (ERI-2594).
      {
        path: 'dark-sky-locations',
        Component: Locations,
        loader: async () => {
          const { getAllLocations } = await import('./lib/locations');
          return getAllLocations();
        },
      },
      {
        path: 'dark-sky-locations/:slug',
        Component: LocationDetail,
        loader: async ({ params }) => {
          const { loadLocationPage } = await import('./lib/locations');
          return loadLocationPage(params.slug);
        },
        getStaticPaths: async () => {
          const { getAllLocationSlugs } = await import('./lib/locations');
          const slugs = await getAllLocationSlugs();
          return slugs.map((slug) => `dark-sky-locations/${slug}`);
        },
      },
      // Country and US state hubs (ERI-2853). Their extra path segments keep
      // them clear of dark-sky-locations/:slug, which matches one segment, and
      // the build check fails if a location is ever slugged "in".
      {
        path: 'dark-sky-locations/in/:country',
        Component: LocationHub,
        loader: async ({ params }) => {
          const { loadLocationHub } = await import('./lib/locations');
          return loadLocationHub(params.country);
        },
        getStaticPaths: async () => {
          const { getHubPaths } = await import('./lib/locations');
          return getHubPaths('country');
        },
      },
      {
        path: 'dark-sky-locations/in/united-states/:state',
        Component: LocationHub,
        loader: async ({ params }) => {
          const { loadLocationHub } = await import('./lib/locations');
          return loadLocationHub('united-states', params.state);
        },
        getStaticPaths: async () => {
          const { getHubPaths } = await import('./lib/locations');
          return getHubPaths('state');
        },
      },
      { path: 'resources', Component: Resources },
      { path: 'changelog', Component: Changelog },
      { path: 'best-milky-way-locations-colorado', Component: LocationGuidePage },
      { path: 'best-milky-way-locations-utah', Component: LocationGuidePage },
      { path: 'best-milky-way-locations-new-mexico', Component: LocationGuidePage },
      { path: 'best-milky-way-locations-arizona', Component: LocationGuidePage },
      { path: 'terms', Component: TermsOfService },
      { path: 'privacy', Component: PrivacyPolicy },
      { path: 'security', Component: Security },
      { path: '*', Component: NotFound },
    ],
  },
];
